import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import { Loader2, ArrowRight, Check, Eye, EyeOff, AlertCircle, Lock } from 'lucide-react';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { TIERS } from '../constants/tiers';
import { PRICING_CONTENT } from '../constants/pricingContent';
import { useAuth } from '../context/AuthContext';
import { trackViewPricing, trackBeginCheckout } from '../lib/analytics';
import { getUtm } from '../lib/utm';

/**
 * iter 99 - Paid signup → Stripe checkout flow.
 *
 * URL: /checkout/:tier?period=onetime|monthly|annual
 *
 * The user lands here from the homepage pricing strip after picking a paid
 * plan. They fill a single 5-field form (name, email, password, ToS, email
 * consent). On submit:
 *   1. POST /api/auth/paid-signup creates the user row with
 *      role=pending_payment + a JWT auth cookie + token in localStorage.
 *   2. We redirect window.location to the returned `checkout_url`
 *      (Stripe Checkout Session). When Stripe sends them back to
 *      /portal/dashboard, `activate-from-session` flips the role to the
 *      paid tier and the dashboard renders for the right plan immediately.
 *
 * Free-trial signup at `/start` is untouched - that stays the explicit
 * "Start Free" path.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const RECURRING_TIERS = new Set(['community', 'connected']);

const PaidCheckout = () => {
  const { tier: tierIdRaw } = useParams();
  const tierId = (tierIdRaw || '').toLowerCase();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const tier = TIERS[tierId];
  const isRecurring = RECURRING_TIERS.has(tierId);
  const period = useMemo(() => {
    const raw = (search.get('period') || '').toLowerCase();
    if (isRecurring) return raw === 'annual' ? 'annual' : 'monthly';
    return 'onetime';
  }, [search, isRecurring]);

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    terms_accepted: false,
    email_consent: false,
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');

  // Invalid tier → bounce to home pricing.
  useEffect(() => {
    if (!tier) navigate('/#pricing', { replace: true });
  }, [tier, navigate]);

  // iter 118 - fire GA4 `view_pricing` once when the per-tier checkout
  // page mounts. This is one of two legitimate "user is looking at
  // pricing" signals (the other being the homepage Pricing section).
  useEffect(() => {
    if (tier) {
      try { trackViewPricing(); } catch { /* analytics is fire-and-forget */ }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!tier) return null;

  // Display price
  const displayPrice = isRecurring
    ? period === 'annual'
      ? tier.priceAnnual
      : tier.price
    : tier.price;
  const displayInterval = isRecurring
    ? period === 'annual'
      ? '/year'
      : '/month'
    : ' one-time';

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const passwordStrength = (pw) => {
    if (!pw) return null;
    let score = 0;
    if (pw.length >= 8) score += 1;
    if (pw.length >= 12) score += 1;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 1;
    if (/\d/.test(pw)) score += 1;
    if (/[^A-Za-z0-9]/.test(pw)) score += 1;
    return Math.min(score, 4);
  };
  const pwScore = passwordStrength(form.password);

  const submit = async (e) => {
    e.preventDefault();
    setServerError('');
    const next = {};
    if (!form.first_name.trim()) next.first_name = 'First name required.';
    if (!form.last_name.trim()) next.last_name = 'Last name required.';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      next.email = 'A valid email is required.';
    if (form.password.length < 8) next.password = 'Password must be at least 8 characters.';
    if (!form.terms_accepted) next.terms_accepted = 'You must accept the Terms.';
    if (!form.email_consent) next.email_consent = 'Please confirm to continue.';
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }

    setLoading(true);
    try {
      const ref =
        sessionStorage.getItem('mundalo_ref') ||
        new URLSearchParams(window.location.search).get('ref') ||
        undefined;

      const r = await fetch(`${API_URL}/api/auth/paid-signup`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          email: form.email.trim().toLowerCase(),
          password: form.password,
          tier: tierId,
          billing_period: period,
          terms_accepted: form.terms_accepted,
          email_consent: form.email_consent,
          success_path: '/portal/dashboard',
          cancel_path: `/checkout/${tierId}?period=${period}`,
          ref,
          utm: getUtm(),
        }),
      });
      const data = await r.json();

      if (!data.success) {
        if (data.errors) {
          setErrors(data.errors);
        }
        if (data.reason === 'account_exists') {
          setServerError(
            data.message ||
              'An account already exists for this email. Sign in to manage your plan.',
          );
        } else if (data.errors && data.errors._) {
          setServerError(data.errors._);
        } else if (!data.errors) {
          setServerError('Something went wrong. Please try again.');
        }
        setLoading(false);
        return;
      }

      // Persist the token so /portal/dashboard authenticates immediately
      // when Stripe bounces back.
      if (data.token) {
        localStorage.setItem('auth_token', data.token);
      }
      // Optimistic user state - role will be flipped from pending_payment
      // to the paid tier by `activate-from-session` on the success redirect.
      if (data.user_id) {
        setUser({
          id: data.user_id,
          email: data.email,
          first_name: form.first_name.trim(),
          last_name: form.last_name.trim(),
          role: 'pending_payment',
        });
      }

      // iter 118 - fire GA4 `begin_checkout` the instant before we hand
      // control to Stripe. Tier name + value mirror the canonical map in
      // /constants/tiers.js so funnel conversion is computable in GA4
      // without joining on price IDs.
      try {
        trackBeginCheckout({
          plan_name: tier.name,
          value: displayPrice,
          currency: 'USD',
        });
      } catch { /* analytics is fire-and-forget */ }

      // Hand off to Stripe.
      window.location.href = data.checkout_url;
    } catch (err) {
      setServerError('Network error. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#F9F6EF] min-h-screen flex flex-col">
      <SEO
        title={`Checkout - ${tier.name} | Mundalo`}
        description={`Complete your ${tier.name} signup and unlock your Mundalo plan.`}
        path={`/checkout/${tierId}`}
      />
      <Header />

      <main className="flex-1 py-10 md:py-16 px-4 sm:px-8" data-testid="paid-checkout-page">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-8 lg:gap-12 items-start">
          {/* ====== LEFT: form ====== */}
          <div className="bg-white border border-[#EFE6CF] rounded-2xl shadow-sm p-6 sm:p-9">
            <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#1A5C6E] mb-3">
              Step 1 of 2 · Create your account
            </p>
            <h1 className="font-display italic text-3xl sm:text-4xl text-[#0A1F2E] leading-tight mb-2">
              Almost there.
            </h1>
            <p className="text-[#0A1F2E]/65 text-sm sm:text-base mb-7 max-w-md">
              Your account is created before checkout, so the moment your payment clears, your plan is live.
            </p>

            <form onSubmit={submit} className="space-y-4" data-testid="paid-checkout-form" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  id="first_name"
                  label="First name"
                  value={form.first_name}
                  onChange={(v) => setField('first_name', v)}
                  error={errors.first_name}
                  autoComplete="given-name"
                  testId="paid-first-name"
                />
                <Field
                  id="last_name"
                  label="Last name"
                  value={form.last_name}
                  onChange={(v) => setField('last_name', v)}
                  error={errors.last_name}
                  autoComplete="family-name"
                  testId="paid-last-name"
                />
              </div>

              <Field
                id="email"
                label="Email"
                type="email"
                value={form.email}
                onChange={(v) => setField('email', v)}
                error={errors.email}
                autoComplete="email"
                testId="paid-email"
              />

              <div>
                <label htmlFor="password" className="block text-[13px] font-medium text-[#0A1F2E] mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={(e) => setField('password', e.target.value)}
                    placeholder="At least 8 characters"
                    autoComplete="new-password"
                    className={`w-full bg-white border ${
                      errors.password ? 'border-[#C97A4A]' : 'border-[#EFE6CF]'
                    } text-[#0A1F2E] placeholder:text-[#0A1F2E]/35 px-4 py-3 pr-11 rounded-lg focus:border-[#2CA8A0] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/25 transition`}
                    data-testid="paid-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#0A1F2E]/50 hover:text-[#0A1F2E]"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {pwScore != null && (
                  <div className="mt-2 flex items-center gap-2">
                    <div className="flex gap-1 flex-1">
                      {[0, 1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded-full ${
                            i < pwScore
                              ? pwScore <= 1
                                ? 'bg-[#C97A4A]'
                                : pwScore === 2
                                ? 'bg-[#E8A830]'
                                : 'bg-[#2CA8A0]'
                              : 'bg-[#EFE6CF]'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-[11px] text-[#0A1F2E]/55 font-mono">
                      {pwScore <= 1 ? 'Weak' : pwScore === 2 ? 'OK' : pwScore === 3 ? 'Good' : 'Strong'}
                    </span>
                  </div>
                )}
                {errors.password && (
                  <p className="mt-1.5 text-[12px] text-[#C97A4A]">{errors.password}</p>
                )}
              </div>

              {/* Consents */}
              <div className="space-y-2 pt-2">
                <ConsentBox
                  id="terms_accepted"
                  checked={form.terms_accepted}
                  onChange={(v) => setField('terms_accepted', v)}
                  error={errors.terms_accepted}
                  testId="paid-terms"
                >
                  I agree to the{' '}
                  <Link to="/terms" target="_blank" className="text-[#1A5C6E] underline">
                    Terms of Service
                  </Link>{' '}
                  and confirm that all Mundalo content is informational only - not legal, tax, or immigration advice.
                </ConsentBox>
                <ConsentBox
                  id="email_consent"
                  checked={form.email_consent}
                  onChange={(v) => setField('email_consent', v)}
                  error={errors.email_consent}
                  testId="paid-email-consent"
                >
                  I want to receive Mundalo emails - purchase confirmation, plan updates, and helpful tips for moving to Mexico. (You can unsubscribe anytime.)
                </ConsentBox>
              </div>

              {serverError && (
                <div
                  className="flex items-start gap-2 bg-[#C97A4A]/10 border border-[#C97A4A]/40 rounded-lg px-3 py-2.5"
                  data-testid="paid-server-error"
                >
                  <AlertCircle className="w-4 h-4 text-[#C97A4A] mt-0.5 shrink-0" />
                  <p className="text-sm text-[#0A1F2E]">{serverError}</p>
                </div>
              )}

              {/* iter 187.h.3 - Locked "Covers Everything" Guarantee.
                   Same verbatim copy as pricing + Day-3 email. Sits
                   directly above the Continue-to-payment button. */}
              <div
                data-testid="covers-everything-guarantee"
                className="rounded-xl border border-[#E8A830]/40 bg-[#FBF6E8] p-4 md:p-5 mt-2"
              >
                <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#7E5A12] mb-1.5">
                  Our promise
                </p>
                <p className="font-display text-lg text-[#0A1F2E] mb-2">
                  The &ldquo;Covers Everything&rdquo; Guarantee
                </p>
                <p className="font-body text-[13px] text-[#0A1F2E]/85 leading-relaxed">
                  If we don&rsquo;t answer everything you need to actually make this move - your visa, your budget, your city, your family - tell us. We&rsquo;ll fix it, free, until we do. If it still doesn&rsquo;t cover what you need, we&rsquo;ll refund you.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#0A1F2E] hover:bg-[#1A5C6E] disabled:opacity-60 text-white font-mono uppercase text-[12px] tracking-[0.15em] font-semibold py-3.5 rounded-full transition mt-2"
                data-testid="paid-checkout-submit"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Opening Stripe…
                  </>
                ) : (
                  <>
                    Continue to payment <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

              <p className="text-[11px] text-[#0A1F2E]/55 text-center pt-2 flex items-center justify-center gap-1.5">
                <Lock className="w-3 h-3" />
                Payments handled by Stripe · Your card never touches our servers.
              </p>

              <p className="text-[12px] text-[#0A1F2E]/65 text-center pt-1">
                Already have an account?{' '}
                <Link to="/portal" className="text-[#1A5C6E] underline" data-testid="paid-link-login">
                  Sign in
                </Link>{' '}
                - then upgrade from the portal.
              </p>
            </form>
          </div>

          {/* ====== RIGHT: order summary ====== */}
          <aside
            className="bg-[#FCF8EE] border border-[#EFE6CF] rounded-2xl p-6 lg:sticky lg:top-24"
            data-testid="paid-checkout-summary"
          >
            <p className="font-mono uppercase tracking-[0.18em] text-[10px] text-[#E8A830] mb-2">
              Your plan
            </p>
            <h2 className="font-display italic text-2xl text-[#0A1F2E] mb-1">{tier.name}</h2>
            {PRICING_CONTENT[tierId]?.tagline && (
              <p className="italic text-sm text-[#1A5C6E] mb-1.5" data-testid="paid-checkout-tagline">
                {PRICING_CONTENT[tierId].tagline}
              </p>
            )}
            <p className="text-[13px] text-[#0A1F2E]/70 mb-5" data-testid="paid-checkout-description">
              {PRICING_CONTENT[tierId]?.description || tier.description}
            </p>

            <div className="flex items-baseline gap-1 mb-4">
              <span
                className="font-display text-4xl text-[#0A1F2E]"
                data-testid="paid-checkout-price"
              >
                ${displayPrice}
              </span>
              <span className="text-sm text-[#0A1F2E]/55">{displayInterval}</span>
            </div>

            {isRecurring && (
              <div
                className="flex items-center gap-2 bg-white border border-[#EFE6CF] rounded-full p-1 mb-4 text-[11px]"
                data-testid="paid-checkout-billing-toggle"
              >
                <BillingPill
                  active={period === 'monthly'}
                  to={`/checkout/${tierId}?period=monthly`}
                  testId="paid-bill-monthly"
                >
                  Monthly
                </BillingPill>
                <BillingPill
                  active={period === 'annual'}
                  to={`/checkout/${tierId}?period=annual`}
                  testId="paid-bill-annual"
                >
                  Annual · save
                </BillingPill>
              </div>
            )}

            <div className="h-px bg-[#EFE6CF] my-4" />

            <p className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#1A5C6E] mb-2">
              What's included
            </p>
            <ul className="space-y-2 text-sm text-[#0A1F2E]/85">
              {FEATURE_BLURBS[tierId]?.map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-[#2CA8A0] mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div className="h-px bg-[#EFE6CF] my-4" />

            <p className="text-[11px] text-[#0A1F2E]/55 leading-relaxed">
              Looking for the no-card option?{' '}
              <Link to="/start-trial" className="text-[#1A5C6E] underline" data-testid="paid-checkout-free-link">
                Start free for 15 days
              </Link>
              .
            </p>
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
};

const Field = ({ id, label, type = 'text', value, onChange, error, autoComplete, testId }) => (
  <div>
    <label htmlFor={id} className="block text-[13px] font-medium text-[#0A1F2E] mb-1.5">
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      autoComplete={autoComplete}
      className={`w-full bg-white border ${
        error ? 'border-[#C97A4A]' : 'border-[#EFE6CF]'
      } text-[#0A1F2E] placeholder:text-[#0A1F2E]/35 px-4 py-3 rounded-lg focus:border-[#2CA8A0] focus:outline-none focus:ring-2 focus:ring-[#2CA8A0]/25 transition`}
      data-testid={testId}
    />
    {error && <p className="mt-1.5 text-[12px] text-[#C97A4A]">{error}</p>}
  </div>
);

const ConsentBox = ({ id, checked, onChange, error, children, testId }) => (
  <div>
    <label htmlFor={id} className="flex items-start gap-3 cursor-pointer">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-4 h-4 accent-[#2CA8A0] cursor-pointer"
        data-testid={testId}
      />
      <span className="text-[13px] text-[#0A1F2E]/80 leading-relaxed">{children}</span>
    </label>
    {error && <p className="mt-1 text-[12px] text-[#C97A4A] pl-7">{error}</p>}
  </div>
);

const BillingPill = ({ active, to, children, testId }) => (
  <Link
    to={to}
    className={`flex-1 text-center px-3 py-1.5 rounded-full font-mono uppercase tracking-wider transition ${
      active ? 'bg-[#0A1F2E] text-white' : 'text-[#0A1F2E]/65 hover:text-[#0A1F2E]'
    }`}
    data-testid={testId}
  >
    {children}
  </Link>
);

const FEATURE_BLURBS = {
  // iter 176 - Blueprint Only / Essentials / Guided bullets are read
  // from the single-source constants/pricingContent.js. Community /
  // Connected still live inline because they are subscription tiers.
  blueprint_only: PRICING_CONTENT.blueprint_only.bullets,
  essentials:     PRICING_CONTENT.essentials.bullets,
  guided:         PRICING_CONTENT.guided.bullets,
  community: [
    'Community Board + Newcomers Board',
    'Local Deals at local businesses',
    'Book Local Adventures',
    'Events (in-person + online)',
    'Housing + roommate finder',
    'Peer Circles - connect & message expats',
    'Cancel anytime',
  ],
  connected: [
    'Community - included',
    'Monthly expert Q&A sessions',
    'Connected-only events',
    'Priority housing notifications',
  ],
};

export default PaidCheckout;
